import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { http } from "../lib/api";

export const PLACEMENT_LABELS = {
  top: "Topo do site (abaixo do menu)",
  home: "Página inicial (entre seções)",
  video_below: "Vídeo — abaixo do player",
  video_side: "Vídeo — coluna lateral",
  gallery: "Galerias — entre os itens",
  footer: "Rodapé",
  mobile_sticky: "Celular — fixo no fim da tela",
  popunder: "Pop-under (abre ao clicar)",
};

const SIZES = [["320x50", "320×50 (celular fixo)"], ["728x90", "728×90 (leaderboard)"], ["300x250", "300×250 (retângulo)"], ["468x60", "468×60 (banner)"], ["970x250", "970×250 (billboard)"], ["320x100", "320×100 (celular)"], ["160x600", "160×600 (arranha-céu)"]];

const inp = "w-full border border-slate-800 bg-[#0d0c11] px-3 py-2.5 text-sm text-white outline-none focus:border-rose-500";
const Label = ({ children }) => <span className="eyebrow mb-2 block !text-slate-400">{children}</span>;

export const AdForm = ({ ad, onClose, onSaved }) => {
  const [f, setF] = useState({
    name: ad.name || "", placement: ad.placement || "top", mode: ad.mode || "image", link_url: ad.link_url || "", code: ad.code || "",
    popunder_url: ad.popunder_url || "", width: ad.width || 728, height: ad.height || 90, frequency_hours: ad.frequency_hours ?? 24, active: ad.active ?? true,
  });
  const [image, setImage] = useState(null);
  const [saving, setSaving] = useState(false);
  const isPop = f.placement === "popunder";
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const setPlacement = (e) => {
    const p = e.target.value;
    const size = p === "mobile_sticky" ? { width: 320, height: 50 } : {};
    setF({ ...f, ...size, placement: p, mode: p === "popunder" ? (f.mode === "image" ? "url" : f.mode) : f.mode === "url" ? "image" : f.mode });
  };

  const save = async (e) => {
    e.preventDefault();
    if (f.mode === "image" && !image && !ad.has_image) return toast.error("Envie a imagem do banner");
    if (f.mode === "code" && !f.code.trim()) return toast.error("Cole o código do anúncio");
    if (f.mode === "url" && !/^https?:\/\//.test(f.popunder_url)) return toast.error("Informe um link válido (https://...)");
    setSaving(true);
    try {
      const body = { ...f, width: Number(f.width) || 728, height: Number(f.height) || 90, frequency_hours: Number(f.frequency_hours) || 0 };
      const r = ad.id ? await http.put(`/admin/ads/${ad.id}`, body) : await http.post("/admin/ads", body);
      if (image) {
        const fd = new FormData();
        fd.append("image", image);
        await http.post(`/admin/ads/${r.data.id}/image`, fd);
      }
      toast.success("Anúncio salvo");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-none border-rose-900/40 bg-[#0b0a0e] sm:max-w-2xl" data-testid="ad-form-dialog">
        <DialogHeader><DialogTitle className="font-serif-d text-2xl text-white">{ad.id ? "Editar anúncio" : "Novo anúncio"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-5">
          <label className="block"><Label>Nome interno</Label><input required value={f.name} onChange={set("name")} placeholder="ex: ExoClick topo" className={inp} data-testid="ad-name-input" /></label>
          <div className="grid gap-5 sm:grid-cols-2">
            <label className="block"><Label>Posição</Label>
              <select value={f.placement} onChange={setPlacement} className={inp} data-testid="ad-placement-select">
                {Object.entries(PLACEMENT_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </label>
            <label className="block"><Label>Tipo</Label>
              <select value={f.mode} onChange={set("mode")} className={inp} data-testid="ad-mode-select">
                {isPop ? <option value="url">Link (abre em nova aba)</option> : <option value="image">Banner de imagem</option>}
                <option value="code">Código HTML/JS da rede de anúncios</option>
              </select>
            </label>
          </div>

          {f.mode === "image" && (
            <div className="grid gap-5 sm:grid-cols-2">
              <label className="block"><Label>Imagem do banner {ad.has_image && "(já enviada)"}</Label>
                <input type="file" accept="image/*" onChange={(e) => setImage(e.target.files?.[0] || null)} className="text-sm text-slate-300 file:mr-3 file:border-0 file:bg-slate-800 file:px-3 file:py-2 file:text-white" data-testid="ad-image-input" />
              </label>
              <label className="block"><Label>Link ao clicar</Label><input value={f.link_url} onChange={set("link_url")} placeholder="https://..." className={inp} data-testid="ad-link-input" /></label>
            </div>
          )}
          {f.mode === "code" && (
            <label className="block"><Label>Código do anúncio</Label>
              <textarea rows={6} value={f.code} onChange={set("code")} placeholder={'<script src="https://..."></script>'} className={`${inp} font-mono-d text-xs`} data-testid="ad-code-input" />
            </label>
          )}
          {f.mode === "url" && (
            <label className="block"><Label>Link do pop-under</Label><input value={f.popunder_url} onChange={set("popunder_url")} placeholder="https://..." className={inp} data-testid="ad-popunder-url-input" /></label>
          )}

          {!isPop && (
            <div className="grid gap-5 sm:grid-cols-3">
              <label className="block sm:col-span-1"><Label>Tamanho</Label>
                <select value={`${f.width}x${f.height}`} onChange={(e) => { const [w, h] = e.target.value.split("x"); setF({ ...f, width: w, height: h }); }} className={inp} data-testid="ad-size-select">
                  {!SIZES.some(([k]) => k === `${f.width}x${f.height}`) && <option value={`${f.width}x${f.height}`}>Personalizado</option>}
                  {SIZES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </label>
              <label className="block"><Label>Largura</Label><input type="number" value={f.width} onChange={set("width")} className={inp} data-testid="ad-width-input" /></label>
              <label className="block"><Label>Altura</Label><input type="number" value={f.height} onChange={set("height")} className={inp} data-testid="ad-height-input" /></label>
            </div>
          )}
          {isPop && (
            <label className="block"><Label>Mostrar no máximo 1 vez a cada (horas)</Label><input type="number" min="0" value={f.frequency_hours} onChange={set("frequency_hours")} className={inp} data-testid="ad-frequency-input" /></label>
          )}

          <label className="flex items-center gap-3 text-sm text-slate-300">
            <input type="checkbox" checked={f.active} onChange={set("active")} className="h-4 w-4 accent-rose-600" data-testid="ad-active-checkbox" /> Anúncio ativo
          </label>
          <div className="flex gap-3 pt-2">
            <button disabled={saving} className="flex-1 bg-rose-600 px-5 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-white hover:bg-rose-500 disabled:opacity-50" data-testid="ad-save-button">{saving ? "Salvando..." : "Salvar anúncio"}</button>
            <button type="button" onClick={onClose} className="border border-slate-700 px-5 py-3 text-xs uppercase tracking-[0.18em] text-slate-300 hover:text-white" data-testid="ad-cancel-button">Cancelar</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
